/**
 * IndexedDB-backed KeyValueStore for E2EE state (MLS groups, key packages, decrypted
 * DM history). Every value is sealed with AES-GCM under a *non-extractable* key that
 * also lives in IndexedDB: page scripts can use it but never read it out, and the raw
 * database files on disk contain only ciphertext.
 */
import { fromBase64, toBase64, type KeyValueStore } from '@comms/crypto';

const DB_NAME = 'comms-e2ee';
const STORE = 'kv';
const KEY_ID = '__wrapping_key__';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const req = fn(db.transaction(STORE, mode).objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/* Structured values (Uint8Array etc.) are encoded to JSON with tagged byte arrays. */
const encode = (v: unknown) =>
  new TextEncoder().encode(
    JSON.stringify(v, (_k, x: unknown) => (x instanceof Uint8Array ? { __u8: toBase64(x) } : x)),
  );
const decode = (b: ArrayBuffer) =>
  JSON.parse(new TextDecoder().decode(b), (_k, x: unknown) =>
    x && typeof x === 'object' && '__u8' in x ? fromBase64((x as { __u8: string }).__u8) : x,
  ) as unknown;

export class SecureStore implements KeyValueStore {
  private constructor(
    private readonly db: IDBDatabase,
    private readonly key: CryptoKey,
    private readonly prefix: string,
  ) {}

  /** One namespace per user so shared browsers don't mix accounts. */
  static async open(namespace: string): Promise<SecureStore> {
    const db = await openDb();
    let key = await tx<CryptoKey | undefined>(db, 'readonly', (s) => s.get(KEY_ID));
    if (!key) {
      key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
        'encrypt',
        'decrypt',
      ]);
      await tx(db, 'readwrite', (s) => s.put(key, KEY_ID));
    }
    return new SecureStore(db, key, `${namespace}:`);
  }

  async get<T>(k: string): Promise<T | undefined> {
    const rec = await tx<{ iv: Uint8Array<ArrayBuffer>; ct: ArrayBuffer } | undefined>(
      this.db,
      'readonly',
      (s) => s.get(this.prefix + k),
    );
    if (!rec) return undefined;
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: rec.iv }, this.key, rec.ct);
    return decode(pt) as T;
  }

  async set(k: string, value: unknown): Promise<void> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, this.key, encode(value));
    await tx(this.db, 'readwrite', (s) => s.put({ iv, ct }, this.prefix + k));
  }

  async delete(k: string): Promise<void> {
    await tx(this.db, 'readwrite', (s) => s.delete(this.prefix + k));
  }

  async keys(prefix: string): Promise<string[]> {
    const all = (await tx<IDBValidKey[]>(this.db, 'readonly', (s) => s.getAllKeys())) as string[];
    return all
      .filter((k) => k.startsWith(this.prefix + prefix))
      .map((k) => k.slice(this.prefix.length));
  }
}
